import React, { useRef, useState, useCallback, useEffect } from 'react'
import _ from 'lodash'
import { animated, useTransition } from 'react-spring'
import useElementDimensions from '../../../hooks/useElementDimensions'

type FadeInOutProps = {
  children: React.ReactNode
  isVisible: boolean
  duration?: number
  className?: string
  style?: Record<string, unknown>
}

const FadeInOut = ({
  children,
  isVisible = false,
  duration = 300,
  style = {},
  className = '',
}: FadeInOutProps) => {
  const transitions = useTransition(isVisible, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration },
  })

  return (
    <>
      {transitions((styleTransition, item) =>
        item ? (
          <animated.div
            style={{ ...styleTransition, ...style }}
            className={className}
          >
            {children}
          </animated.div>
        ) : null
      )}
    </>
  )
}

type TransitionProps = {
  children: React.ReactNode
  isVisible: boolean
  from: Record<string, unknown>
  enter: Record<string, unknown>
  leave: Record<string, unknown>
  config: Record<string, unknown>
  update?: Record<string, unknown>
  onRest?: () => void
  style?: Record<string, unknown>
}

/**
 * Animate transitions with custom settings
 */
const Transition = ({
  children,
  isVisible = false,
  from,
  enter,
  leave,
  config,
  update,
  onRest,
  style = {},
}: TransitionProps) => {
  const transitions = useTransition(isVisible, {
    from,
    enter,
    leave,
    config,
    update,
    onRest,
  })

  return (
    <>
      {transitions((styleTransition, item) =>
        item ? (
          <animated.div style={{ ...styleTransition, ...style }}>
            {children}
          </animated.div>
        ) : null
      )}
    </>
  )
}

type FadeInOutWithHeightProps = {
  children: React.ReactNode
  isVisible: boolean
  duration?: number
  className?: string
  style?: Record<string, unknown>
  height?: number
  contentAdjustment?: number
}

const FadeInOutWithHeight: React.FC<FadeInOutWithHeightProps> = ({
  children,
  isVisible = false,
  duration = 300,
  style = {},
  className = '',
  height = 0,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const dimensions = useElementDimensions(ref)
  const [_height, setHeight] = useState(height)
  const [_width, setWidth] = useState(0)

  /**
   * "measures" the rendered height of the element and tracks that as current height.
   * Looks at current, child and grandchild to ensure it finds the correct height.
   */
  const setNewHeight = useCallback(() => {
    const debouncedSetHeight = _.debounce(() => {
      if (isVisible && ref.current) {
        const measuredHeight = Math.max(
          ref.current.offsetHeight,
          (ref.current.childNodes[0] as HTMLElement)?.offsetHeight,
          (
            (ref.current.childNodes[0] as HTMLElement)
              ?.childNodes[0] as HTMLElement
          )?.offsetHeight
        )
        if (measuredHeight !== _height) {
          setHeight(measuredHeight)
        }
      }
    }, 400)
    debouncedSetHeight()
  }, [_height, isVisible])

  /**
   * This effect is to update the height when the width changes.
   * Needed when changing width causes wrapping and therefore height changes.
   */
  useEffect(() => {
    if (dimensions?.width !== _width) {
      setWidth(dimensions?.width || 0)
      setNewHeight()
    }
  }, [_width, dimensions?.width, setNewHeight])

  const transitions = useTransition(isVisible, {
    from: { opacity: 0, height: 0 },
    enter: { opacity: 1, height: _height },
    leave: { opacity: 0, height: 0 },
    config: { duration },
    onRest: () => {
      setNewHeight()
    },
    update: { height: _height }, // Ensure dynamic update of height
  })

  return (
    <>
      {transitions((styleTransition, item) =>
        item ? (
          <animated.div
            ref={ref}
            /**
             * This is to apply the transition style only while animating.
             * Once animation is done, we fall back on it's flow height.
             */
            style={{
              ...(styleTransition?.height?.animation?.values[0]?.done
                ? {}
                : styleTransition),
              ...style,
            }}
            className={className}
          >
            {children}
          </animated.div>
        ) : null
      )}
    </>
  )
}

const Animate = {
  FadeInOut,
  Transition,
  FadeInOutWithHeight,
}

export default Animate
